import React, { createContext, useCallback, useContext, useMemo, useState } from 'react';
import { useLocation } from 'react-router-dom';

// Pages that ship (or will ship) "on this page" help content. Anything else
// (e.g. a GA-only route with no content yet) still gets a working panel —
// just an empty topics list, search and support still work everywhere.
const ROUTE_KEYS = [
  'dashboard', 'products', 'inventory', 'categories', 'client-orders',
  'invoices', 'reporting', 'coupons', 'advertisements', 'users', 'subscription',
];

export const routeKeyFromPathname = (pathname) => {
  const segment = (pathname || '').replace(/^\/+/, '').split('/')[0];
  return ROUTE_KEYS.includes(segment) ? segment : (segment || 'dashboard');
};

const HelpContext = createContext(null);

// `{ isOpen, view, route, activeArticleId, open(topicId?), close, openArticle,
//   openSearch, openSupport, goBack, ... }` — mounted once per
// design_handoff_help_system/README.md. `open()` with no id shows the
// current route's topics; `open(id)` jumps straight to that article (used by
// HelpMark's "Read more" and locked-nav pages opening straight to the plans
// article). Remembers the last article read per route so leaving and coming
// back to the SAME page resumes it, while landing on a DIFFERENT page always
// starts at that page's topics.
export const HelpProvider = ({ children }) => {
  const location = useLocation();
  const route = routeKeyFromPathname(location.pathname);

  const [isOpen, setIsOpen] = useState(false);
  const [view, setView] = useState('topics'); // 'topics' | 'article' | 'search' | 'support'
  const [activeArticleId, setActiveArticleId] = useState(null);
  // What `goBack()` restores: a view (topics/search), or another article
  // (one hop — following a related-article link and pressing back returns to
  // the article you came from; a second back press falls through to topics,
  // which covers every path the panel's screens actually show).
  const [previousEntry, setPreviousEntry] = useState({ view: 'topics', articleId: null });
  const [searchQuery, setSearchQuery] = useState('');
  const [lastArticleByRoute, setLastArticleByRoute] = useState({});
  // The route currently showing a HelpTour overlay (step 9, layer 3), or
  // null. A route, not a boolean — only one tour can run at a time, and it's
  // always for the current page. Closing the panel and running a tour are
  // independent (the panel's own drawer stays as-is behind the tour overlay).
  const [activeTour, setActiveTour] = useState(null);

  const rememberArticle = useCallback((articleId) => {
    setLastArticleByRoute((prev) => (prev[route] === articleId ? prev : { ...prev, [route]: articleId }));
  }, [route]);

  const open = useCallback((articleId) => {
    setIsOpen(true);
    if (articleId) {
      setActiveArticleId(articleId);
      setPreviousEntry({ view: 'topics', articleId: null });
      setView('article');
      rememberArticle(articleId);
    } else {
      setView('topics');
    }
  }, [rememberArticle]);

  const close = useCallback(() => setIsOpen(false), []);

  const openArticle = useCallback((articleId, from) => {
    setIsOpen(true);
    setPreviousEntry(from === 'article'
      ? { view: 'article', articleId: activeArticleId }
      : { view: from || view, articleId: null });
    setActiveArticleId(articleId);
    setView('article');
    rememberArticle(articleId);
  }, [rememberArticle, view, activeArticleId]);

  const openSearch = useCallback((query = '') => {
    setIsOpen(true);
    setSearchQuery(query);
    setPreviousEntry({ view: 'topics', articleId: null });
    setView('search');
  }, []);

  const openSupport = useCallback(() => {
    setIsOpen(true);
    setPreviousEntry({ view, articleId: view === 'article' ? activeArticleId : null });
    setView('support');
  }, [view, activeArticleId]);

  const showTopics = useCallback(() => setView('topics'), []);

  const startTour = useCallback((tourRoute) => {
    setIsOpen(false); // the tour overlay covers the page; the panel would just double up behind it
    setActiveTour(tourRoute || route);
  }, [route]);

  const closeTour = useCallback(() => setActiveTour(null), []);

  const goBack = useCallback(() => {
    if (previousEntry.articleId) {
      setActiveArticleId(previousEntry.articleId);
      setView('article');
      setPreviousEntry({ view: 'topics', articleId: null });
    } else {
      setView(previousEntry.view || 'topics');
    }
  }, [previousEntry]);

  const value = useMemo(() => ({
    isOpen,
    view,
    route,
    activeArticleId,
    searchQuery,
    lastArticleByRoute,
    lastArticleForRoute: lastArticleByRoute[route] || null,
    activeTour,
    open,
    close,
    openArticle,
    openSearch,
    openSupport,
    showTopics,
    goBack,
    setSearchQuery,
    startTour,
    closeTour,
  }), [isOpen, view, route, activeArticleId, searchQuery, lastArticleByRoute, activeTour, open, close, openArticle, openSearch, openSupport, showTopics, goBack, startTour, closeTour]);

  return <HelpContext.Provider value={value}>{children}</HelpContext.Provider>;
};

export const useHelp = () => {
  const ctx = useContext(HelpContext);
  if (!ctx) throw new Error('useHelp must be used within a HelpProvider');
  return ctx;
};

export default HelpContext;
